import { parse, ParseResult } from 'papaparse';

import { blankStudent } from '../constants/inviteStudentsDefaults';
import { Student } from '../types/inviteStudentsTypes';
import { UserRoles } from '../../../components/roster/types';

// * `papaparse` is not in TypeScript, so it isn't aware that `data` is a multidimensional array (and thus data[0] is a string[])
const isClassHeaderRow = (headerRow): { hasHeaderRow: boolean; columnIndexes: Record<string, number | undefined> } => {
    // expected order when no header row is present
    const defaultIndexes = {
        firstName: 0,
        lastName: 1,
        email: 2,
        role: 3
    };

    const columnIndexes: Record<string, undefined | number> = {
        firstName: undefined,
        lastName: undefined,
        email: undefined,
        role: undefined
    };

    // Column header should _contain_ an accepted, case-insensitive header value
    const acceptedFirstNameHeaders = ['first', 'firstname', 'first name', 'first_name', 'name, first'];
    const acceptedLasttNameHeaders = ['last', 'lastname', 'last name', 'last_name', 'name, last'];
    const acceptedEmailHeaders = ['email', 'e-mail', 'email address', 'email_address'];
    const acceptedRoleHeaders = ['role'];

    headerRow.forEach((key: string, index: number) => {
        const keyTrimmed = key.trim().toLowerCase();

        if (acceptedFirstNameHeaders.includes(keyTrimmed)) {
            columnIndexes.firstName = index;
        }
        if (acceptedLasttNameHeaders.includes(keyTrimmed)) {
            columnIndexes.lastName = index;
        }
        if (acceptedEmailHeaders.includes(keyTrimmed)) {
            columnIndexes.email = index;
        }
        if (acceptedRoleHeaders.includes(keyTrimmed)) {
            columnIndexes.role = index;
        }
    });

    const hasHeaderRow = Object.values(columnIndexes).some((i) => i !== undefined);

    if (hasHeaderRow) {
        return { hasHeaderRow, columnIndexes };
    }

    return { hasHeaderRow, columnIndexes: defaultIndexes };
};

/**
 * Format the parsed data object from the uploaded CSV into an array of Students
 */
const parseStudentsCsv = (csvText: string): Student[] => {
    const parsedHeaderRow: ParseResult<string> = parse(csvText, {
        header: false,
        preview: 1,
        skipEmptyLines: 'greedy'
    });

    if (parsedHeaderRow.data.length === 0) return [{ ...blankStudent }];

    const { hasHeaderRow, columnIndexes } = isClassHeaderRow(parsedHeaderRow.data[0]);

    // Parse to array of arrays (when config property header: true, parse returns array of keyed objects)
    // We are avoiding that to enforce uniformity in the headers / keys of the returned object and to allow us to pick from mulitple columns
    const { data }: ParseResult<string> = parse(csvText, { header: false, skipEmptyLines: 'greedy' });

    if (hasHeaderRow) data.shift(); // pop header row from data

    // Translate [[]] to Student[]
    return data.map((studentData) => {
        const getTrimmedValue = (index: number | undefined) =>
            typeof index === 'number' ? studentData[index]?.trim() : undefined;

        return {
            firstName: getTrimmedValue(columnIndexes.firstName),
            lastName: getTrimmedValue(columnIndexes.lastName),
            email: getTrimmedValue(columnIndexes.email),
            role: getTrimmedValue(columnIndexes.role) as UserRoles | undefined
        };
    }) as Student[];
};

export default parseStudentsCsv;
